'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import { Box, HStack, VStack, Text, Icon } from '@ui-library';
import { Ban } from '@ui-library/icons';

import useLocalization from '@/hooks/useLocalization';
import { useCancellationPolicy } from '@/hooks/queries/useCancellationPolicy';
import { ReservationWithRateDetailsDTO } from '@/models';
import { toApiLanguageCode } from '@/providers/i18n/utils';

import { getCancellationPolicyView } from './cancellationPolicyText';
import { CancellationPolicyErrorBoundary } from './CancellationPolicyErrorBoundary';

type CancellationPolicyCardProps = {
  reservation: ReservationWithRateDetailsDTO;
  isOpen: boolean;
};

const CancellationPolicyCardContent: React.FC<CancellationPolicyCardProps> = ({
  reservation,
  isOpen,
}) => {
  const intl = useIntl();
  const { locale } = useLocalization();

  const { policyInformation } = useCancellationPolicy({
    propertyId: reservation.propertyId,
    reservationId: reservation.id,
    languageCode: toApiLanguageCode(locale),
    enabled: isOpen,
  });

  const view = getCancellationPolicyView(policyInformation);
  if (!view) {
    return null;
  }

  return (
    <Box
      borderRadius={'md'}
      border={'solid 1px'}
      borderColor={'fog.600'}
      bg={'fog.200'}
      p={'4'}
      w={'full'}
      mt={'4'}
    >
      <HStack gap={2} alignItems={'flex-start'}>
        <Icon color="fog.800">
          <Ban size={20} />
        </Icon>
        <VStack gap={1} alignItems={'flex-start'} minW={0} w={'full'}>
          <Text fontWeight={'semibold'} color="fog.800">
            {intl.formatMessage({
              id: 'edit_reservation/reservation_details/cancellation_policy_label',
              defaultMessage: 'Cancellation policy',
            })}
          </Text>
          {view.label && (
            <Text fontWeight={'semibold'} color="fog.800">
              {view.label}
            </Text>
          )}
          {view.cancellationText && (
            <Text color="fog.800" overflowWrap="anywhere">
              {view.cancellationText}
            </Text>
          )}
          {view.additionalPolicyText && (
            <Text color="fog.800" overflowWrap="anywhere">
              {view.additionalPolicyText}
            </Text>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

export const CancellationPolicyCard: React.FC<CancellationPolicyCardProps> = (
  props,
) => (
  <CancellationPolicyErrorBoundary>
    <CancellationPolicyCardContent {...props} />
  </CancellationPolicyErrorBoundary>
);
